import {
  BANDS_DJS_TAXONOMY_KEY,
  CAKES_DESSERTS_TAXONOMY_KEY,
  CATERING_TAXONOMY_KEY,
  FLORISTS_TAXONOMY_KEY,
  HAIR_MAKEUP_TAXONOMY_KEY,
  PHOTOGRAPHERS_TAXONOMY_KEY,
  VENUES_TAXONOMY_KEY,
  VIDEOGRAPHERS_TAXONOMY_KEY,
  SearchableVendorTaxonomyKey,
  PLANNERS_TAXONOMY_KEY,
  BAR_SERVICES_TAXONOMY_KEY,
} from '@zola-helpers/client/dist/es/marketplace/vendorTaxonomyKeys';
import { getVendorTaxonomySwitchFunc } from '@zola-helpers/client/dist/es/marketplace/vendorUtils';

import _omitBy from 'lodash/omitBy';
import _pick from 'lodash/pick';

import { MappedInquiryPreferencesView } from '~/types/responseTypes';
import { RemoveNonNullables } from '~/types/utils';

import { PARTY_SIZE } from '../questions/CustomerCount/CustomerCountStep';
import {
  VENUE_FIELD_NAMES,
  FIELD_NAMES as REVIEW_AND_SUBMIT_FIELD_NAMES,
} from '../questions/WeddingInfo/WeddingInfoQuestion';
import { WEDDING_PARTY } from '../questions/WeddingParty/WeddingPartyStep';
import { InquiryFormValues } from '../types';

interface VendorPreferencesFacetsKeys {
  preferences: string[];
}

/**
 * Strips out values from inquiry preferences depending on vendor type
 *
 * @param taxonomyKey {string} vendor type for a given storefront
 *
 * @returns {Record<string, any} a new entity object
 *
 * @deprecated Please move the functionality to individual question steps with initializeFormValues and buildInquiryRequest
 */
export const vendorInquiryPreferencesKeys = (
  taxonomyKey: SearchableVendorTaxonomyKey
): VendorPreferencesFacetsKeys => {
  /** list of values that is common for all vendor types */
  const commonPrefs: (keyof InquiryFormValues)[] = [
    ...(taxonomyKey === VENUES_TAXONOMY_KEY ? VENUE_FIELD_NAMES : REVIEW_AND_SUBMIT_FIELD_NAMES),
    'bookedDates',
    'weddingDate',
    'weddingYearMonthDate',
    'weddingEndPriceCents',
    'serviceStartPrice',
    'serviceEndPrice',
  ] as (keyof InquiryFormValues)[];

  /** price explorers is used in many vendor types, but not all */
  const priceExplorersFieldNames = ['flexibleOnPrice'];

  const facetAndPreferences = getVendorTaxonomySwitchFunc<
    SearchableVendorTaxonomyKey,
    VendorPreferencesFacetsKeys
  >({
    [VENUES_TAXONOMY_KEY]: {
      preferences: [...priceExplorersFieldNames],
    },
    [PHOTOGRAPHERS_TAXONOMY_KEY]: {
      preferences: [...priceExplorersFieldNames],
    },
    [VIDEOGRAPHERS_TAXONOMY_KEY]: {
      preferences: [...priceExplorersFieldNames],
    },
    [CATERING_TAXONOMY_KEY]: {
      preferences: [...priceExplorersFieldNames],
    },
    [FLORISTS_TAXONOMY_KEY]: {
      preferences: [...(WEDDING_PARTY.fieldNames || []), ...priceExplorersFieldNames],
    },
    [HAIR_MAKEUP_TAXONOMY_KEY]: {
      preferences: [...(PARTY_SIZE.fieldNames || []), ...priceExplorersFieldNames],
    },
    [BANDS_DJS_TAXONOMY_KEY]: {
      preferences: [...priceExplorersFieldNames],
    },
    [CAKES_DESSERTS_TAXONOMY_KEY]: {
      preferences: [...priceExplorersFieldNames],
    },
    [PLANNERS_TAXONOMY_KEY]: {
      preferences: [...priceExplorersFieldNames],
    },
    [BAR_SERVICES_TAXONOMY_KEY]: {
      preferences: [...priceExplorersFieldNames],
    },
  });

  const { preferences } = facetAndPreferences(taxonomyKey);

  return {
    preferences: [...commonPrefs, ...preferences],
  };
};

/**
 * Strips out values from inquiry preferences depending on vendor type
 *
 * @param taxonomyKey {string} vendor type for a given storefront
 * @param inquiryPreferences
 *
 * @returns {Record<string, any} a new entity object
 *
 * @deprecated Please move the functionality to individual question steps with initializeFormValues and buildInquiryRequest
 */
export const convertInquiryPreferencesToFormValues = (
  taxonomyKey: SearchableVendorTaxonomyKey,
  inquiryPreferences: MappedInquiryPreferencesView
): Partial<InquiryFormValues> => {
  const { preferences } = vendorInquiryPreferencesKeys(taxonomyKey);

  //
  // This is some typescript magic!
  //
  // MappedInquiryPreferenceView can have _null_ fields.  Not surprising, if you've
  // never inquired with this vendor type, you'd expect a null.  Never inquired
  // with a beautician?   flexibleOnWeddingPartyCount should be null.  So the
  // types _are correct_ for MappedInquiryPreferencesView.  But React Final
  // Form, in the default configuration, just removes null values from the form
  // so instead of flexibleOnWeddingPartyCount: null | boolean we have
  // flexibleOnWeddingPartyCount?: boolean in the form values. It _sucks_ to
  // add `| null` to all the types so instead, we _throw out the nulls_ before
  // we initialize the form.  The magic for typescript is RemoveNonNullables which
  // makes all fields you provide (we provide all of them) as not allowing null.
  // And we pair this with _omitBy to make sure we aren't passing in a null
  //
  // Good luck brave reader.
  //
  const preferencesWithoutNullValues: Partial<
    RemoveNonNullables<MappedInquiryPreferencesView, keyof MappedInquiryPreferencesView>
  > = _omitBy(_pick(inquiryPreferences, preferences), (value) => value === null);

  return {
    ...preferencesWithoutNullValues,
    /**
     * we don't want to cache the previous inquiries priceMatch value,
     * each inquiry will start out empty to record the correct value.
     */
    priceMatch: undefined,
  };
};
